import type {
  CoachingPartnership,
  CoachingPartnershipMessage,
  CoachProfile,
} from '@efdigital/ll-addon-data';
import { queryCoachProfilesByPartnerships } from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import { queryCoachingPartnershipsByCoach } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-partnerships';
import { queryPartnershipMessages } from '@efdigital/ll-xceed-v2/data/builders/coaching/partnership-messages';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import type { CoachingPlanner } from '@efdigital/ll-xceed-v2/utils/coaching/coachee/coaching-plan-manager';
import {
  COACH_MESSAGEABLE_PARTNERSHIP_STATUSES,
  initials,
  messageablePartnerships,
  PartnershipConversation,
  sortMessages,
} from '@efdigital/ll-xceed-v2/utils/coaching/partnership-messages';
import type { Store } from '@warp-drive/core';

async function messagesForPartnerships(
  store: Store,
  partnerships: CoachingPartnership[]
): Promise<ReturnType<typeof sortMessages>[]> {
  return Promise.all(
    partnerships.map(async (partnership) => {
      const response = await store.request(
        queryPartnershipMessages(partnership.id!)
      );
      return sortMessages(response.content.data);
    })
  );
}

export async function loadCoachPartnershipConversations(
  store: Store,
  coach: CoachService,
  coachUserId: string
): Promise<PartnershipConversation[]> {
  const [partnershipsResponse, coachees] = await Promise.all([
    store.request(
      queryCoachingPartnershipsByCoach(
        coachUserId,
        COACH_MESSAGEABLE_PARTNERSHIP_STATUSES
      )
    ),
    coach.getCoachees(),
  ]);
  const partnerships = messageablePartnerships(
    partnershipsResponse.content.data,
    'coach'
  );
  const messages = await messagesForPartnerships(store, partnerships);

  return partnerships.map((partnership, index) => {
    const coachee = coachees.find(
      (candidate) => candidate.userId === partnership.user
    );
    const firstName = coachee?.givenName ?? 'Coachee';
    const lastName = coachee?.surname ?? '';

    return new PartnershipConversation({
      partnership,
      participant: {
        firstName,
        initials: initials(firstName, lastName),
        name: coachee?.displayName ?? `${firstName} ${lastName}`.trim(),
        secondary: 'Coachee',
      },
      messages: messages[index]!,
    });
  });
}

export async function loadCoacheePartnershipConversations(
  store: Store,
  planner: CoachingPlanner
): Promise<PartnershipConversation[]> {
  const partnerships = planner.messagingPartnerships;
  const [profilesResponse, messages] = await Promise.all([
    partnerships.length
      ? store.request(queryCoachProfilesByPartnerships(partnerships))
      : Promise.resolve(null),
    messagesForPartnerships(store, partnerships),
  ]);
  const profiles = profilesResponse?.content.data ?? [];

  return partnerships.map((partnership, index) => {
    const profile = profiles.find(
      (candidate: CoachProfile) => candidate.user === partnership.coachUserId
    );
    const firstName = profile?.firstName ?? 'Coach';
    const lastName = profile?.lastName ?? '';

    return new PartnershipConversation({
      partnership,
      participant: {
        firstName,
        initials: initials(firstName, lastName),
        name: profile
          ? [profile.prefix, firstName, lastName, profile.suffix]
              .filter(Boolean)
              .join(' ')
          : 'Coach',
        secondary: 'Coach',
        coach: profile,
      },
      messages: messages[index]!,
    });
  });
}

/** Identifies a message's content for change detection, including read
 *  state, so a read receipt with no new message is still detected. */
function messageSignature(
  messages: readonly CoachingPartnershipMessage[]
): string {
  return messages
    .map(
      (message) =>
        `${message.id}:${message.readByCoachAt ?? ''}:${message.readByCoacheeAt ?? ''}`
    )
    .join('|');
}

export function mergePartnershipConversationMessages(
  current: PartnershipConversation[],
  incoming: PartnershipConversation[]
): boolean {
  if (!current.length) {
    if (!incoming.length) {
      return false;
    }

    current.push(...incoming);
    return true;
  }

  let changed = false;
  const byPartnershipId = new Map(
    current.map((conversation) => [conversation.partnership.id!, conversation])
  );
  const incomingIds = new Set(
    incoming.map((conversation) => conversation.partnership.id!)
  );

  for (const nextConversation of incoming) {
    const partnershipId = nextConversation.partnership.id!;
    const existing = byPartnershipId.get(partnershipId);

    if (!existing) {
      current.push(nextConversation);
      changed = true;
      continue;
    }

    if (
      messageSignature(existing.messages) !==
      messageSignature(nextConversation.messages)
    ) {
      existing.messages = nextConversation.messages;
      changed = true;
    }
  }

  for (let index = current.length - 1; index >= 0; index--) {
    if (!incomingIds.has(current[index]!.partnership.id!)) {
      current.splice(index, 1);
      changed = true;
    }
  }

  return changed;
}
